import type { DriverDocType } from "../types/company";
import type { VehicleCard, VehicleDetail } from "../types/vtc";
import { CATEGORIES, ENERGIES, TRANSMISSIONS } from "../data/company";
import { DOC_FROM_API, unslug } from "./companyApi";
import { api } from "./api";
import { getDict } from "../i18n";

/** A published listing as GET /listings, /listings/home and /listings/:slug return it. */
interface ApiCard {
  id: string;
  slug: string;
  brand: string;
  model: string;
  version: string;
  energy: string;
  transmission: string;
  category: string;
  city: string;
  daily?: number;
  weekly: number;
  monthly: number;
  deposit: number;
  minDays: number;
  photo: string | null;
  availableUnits: number;
  fleetSize: number;
  sponsored: boolean;
  company: {
    publicId: number | null;
    verified: boolean;
    rating: number | null;
    reviews: number;
    memberSince?: number | null;
    area?: string;
    listings?: number;
    name?: string | null;
  } | null;
}

interface ApiDetail extends ApiCard {
  year: number | null;
  seats: number;
  colour: string;
  mileage: number;
  description: string;
  kmIncluded: number;
  extraKm: number;
  insurance: string;
  maintenance: string;
  requiredDocs: string[];
  photos: string[];
  similar: ApiCard[];
  reviews: { id: string; author: string; rating: number; text: string; vehicle: string; date: string }[];
}

export interface HomeCategory {
  id: "electric" | "hybrid" | "premium" | "estate";
  count: number;
  /** Up to 3 makes in that category, e.g. ["Tesla", "Hyundai"]. */
  brands: string[];
}

/** Backend slugs ("plug-in-hybrid") back to the labels the UI translates ("Plug-in hybrid"). */
const toCard = (c: ApiCard): VehicleCard => ({
  id: c.id,
  slug: c.slug,
  brand: c.brand,
  model: c.model,
  energy: unslug(c.energy, ENERGIES) as VehicleCard["energy"],
  transmission: unslug(c.transmission, TRANSMISSIONS),
  category: unslug(c.category, CATEGORIES),
  city: c.city,
  daily: c.daily,
  weekly: c.weekly,
  monthly: c.monthly,
  deposit: c.deposit,
  minDays: c.minDays,
  availableUnits: c.availableUnits,
  urgent: c.sponsored,
  photo: c.photo,
  company: {
    name: c.company?.publicId ? getDict().common.companyLabel(c.company.publicId) : "",
    verified: !!c.company?.verified,
    publicId: c.company?.publicId ?? null,
    rating: c.company?.rating ?? null,
    reviews: c.company?.reviews ?? 0,
  },
});

/** GET /listings/home — popular vehicles, category tiles and served cities. */
export async function getHome(): Promise<{ popular: VehicleCard[]; categories: HomeCategory[]; cities: string[] }> {
  const { data } = await api.get<{ popular: ApiCard[]; categories: HomeCategory[]; cities: string[] }>("/listings/home");
  return { popular: data.popular.map(toCard), categories: data.categories, cities: data.cities };
}

/** GET /listings/:slug — the vehicle page. */
export async function getVehicle(slug: string): Promise<VehicleDetail> {
  const { data: d } = await api.get<ApiDetail>(`/listings/${encodeURIComponent(slug)}`);
  return {
    ...toCard(d),
    version: d.version,
    description: d.description,
    year: d.year ?? 0,
    seats: d.seats,
    colour: d.colour,
    mileage: d.mileage,
    fleetSize: d.fleetSize,
    minDays: d.minDays,
    kmIncluded: d.kmIncluded,
    extraKm: d.extraKm,
    conditions: [],
    insurance: d.insurance,
    maintenance: d.maintenance,
    requiredDocs: d.requiredDocs.map((x) => DOC_FROM_API[x]).filter(Boolean) as DriverDocType[],
    companyInfo: {
      memberSince: d.company?.memberSince ?? null,
      area: d.company?.area ?? "",
      listings: d.company?.listings ?? 0,
      realName: d.company?.name ?? null,
    },
    reviews: d.reviews ?? [],
    photos: d.photos,
    similar: d.similar.map(toCard),
  };
}

/** GET /listings — every published listing; the search page filters them locally. */
export async function listVehicles(): Promise<VehicleCard[]> {
  const { data } = await api.get<{ items: ApiCard[] }>("/listings", { params: { limit: 100 } });
  return data.items.map(toCard);
}

/** GET /companies/:publicId — the public company page. */
export interface PublicCompany {
  publicId: number;
  ownerType: "individual" | "company";
  verified: boolean;
  area: string;
  memberSince: number | null;
  stats: { listed: number; available: number; fleet: number };
  rating: { average: number; count: number; distribution: Record<1 | 2 | 3 | 4 | 5, number> };
  /** Registered drivers only; null for visitors. */
  details: { name: string; address: string; phone: string; email: string } | null;
  listings: VehicleCard[];
  reviews: { id: string; author: string; rating: number; text: string; vehicle: string; date: string }[];
}

export async function getCompany(publicId: string): Promise<PublicCompany> {
  const { data } = await api.get<Omit<PublicCompany, "listings"> & { listings: ApiCard[] }>(
    `/companies/${encodeURIComponent(publicId)}`,
  );
  return { ...data, listings: data.listings.map(toCard) };
}

/** GET /driver/favourites — the driver's saved vehicles still published. */
export async function getFavourites(): Promise<VehicleCard[]> {
  const { data } = await api.get<{ items: ApiCard[] }>("/driver/favourites");
  return data.items.map(toCard);
}

export interface CityOption {
  city: string;
  /** Published listings in that city. */
  listings: number;
}

/** GET /listings/cities?q= — cities where companies have published vehicles (accent-insensitive). */
export async function searchCities(q: string, limit = 8): Promise<CityOption[]> {
  const { data } = await api.get<{ items: CityOption[] }>("/listings/cities", { params: { q, limit } });
  return data.items;
}
